import { MESSAGE_TYPE } from 'shared/constants/messages';
import MessageApi from 'dashboard/api/inbox/message';

// Limite do MessageFinder#messages_after: página menor que isso = fim.
const PAGE_SIZE = 100;

// Radix slate — mesmas variáveis do _next-colors.scss (light + dark)
const THEMES = {
  light: {
    bg: '#fcfcfd', // slate-1
    surface: '#ffffff',
    surfaceAlt: '#f9f9fb', // slate-2
    border: '#e8e8ec', // slate-4
    borderSubtle: '#f0f0f3', // slate-3
    textPrimary: '#1c2024', // slate-12
    textSecondary: '#60646c', // slate-11
    textMuted: '#80838d', // slate-10
    badgeOutBg: '#eef0fe', // iris-3
    badgeOutText: '#5b5bd6', // iris-9
    badgeInBg: '#f0f9f0',
    badgeInText: '#3d9a50',
    activityBg: '#f9f9fb',
    activityText: '#80838d',
    primary: '#5b5bd6', // iris-9
    primaryHover: '#5151cd', // iris-10
  },
  dark: {
    bg: '#111113', // slate-1 dark
    surface: '#18191b', // slate-2 dark
    surfaceAlt: '#212225', // slate-3 dark
    border: '#272a2d', // slate-4 dark
    borderSubtle: '#2e3135', // slate-5 dark
    textPrimary: '#edeef0', // slate-12 dark
    textSecondary: '#b0b4ba', // slate-11 dark
    textMuted: '#777b84', // slate-10 dark
    badgeOutBg: '#232348', // iris-3 dark
    badgeOutText: '#9b9ef0', // iris-11 dark
    badgeInBg: '#162316',
    badgeInText: '#65ba75',
    activityBg: '#212225',
    activityText: '#777b84',
    primary: '#5b5bd6', // iris-9
    primaryHover: '#5151cd', // iris-10
  },
};

const isDarkMode = () => document.body.classList.contains('dark');

const formatDate = timestamp => {
  if (!timestamp) return '';
  return new Date(timestamp * 1000).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const escapeHtml = str => {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

const avatarInitials = name => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name[0].toUpperCase();
};

// O store só tem as mensagens já carregadas na tela (paginação de 20, das
// mais novas pra trás) — exportar dali cortava o começo de conversa longa em
// silêncio. Varre a conversa inteira pela API, em ordem cronológica.
export const fetchAllMessages = async conversationId => {
  const byId = new Map();
  let after = 0;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop -- cursor: cada página depende da anterior
    const { data } = await MessageApi.getMessagesAfter({
      conversationId,
      after,
    });
    const page = data.payload || [];
    page.forEach(msg => byId.set(msg.id, msg));
    if (page.length < PAGE_SIZE) break;

    const lastId = page[page.length - 1].id;
    if (lastId === after) {
      throw new Error(`paginação parou de avançar no id ${after}`);
    }
    after = lastId;
  }
  return [...byId.values()].sort(
    (a, b) => a.created_at - b.created_at || a.id - b.id
  );
};

const safeUrl = url => (/^https?:\/\//i.test(url || '') ? url : '');

const fileNameFromUrl = url => {
  const last = (url || '').split('?')[0].split('/').pop();
  if (!last) return '';
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

const ATTACHMENT_LABELS = {
  image: 'Imagem',
  audio: 'Áudio',
  video: 'Vídeo',
  file: 'Arquivo',
  location: 'Localização',
  contact: 'Contato',
};

const renderAttachment = attachment => {
  const url = safeUrl(attachment.data_url);
  const label = ATTACHMENT_LABELS[attachment.file_type] || 'Anexo';

  if (attachment.file_type === 'image' && url) {
    return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer"><img class="att-img" src="${escapeHtml(url)}" alt="${label}" /></a>`;
  }

  if (attachment.file_type === 'audio') {
    const text = (attachment.transcribed_text || '').trim();
    const body = text
      ? `<div class="att-transcript">${escapeHtml(text)}</div>`
      : '<div class="att-missing">Áudio sem transcrição</div>';
    return `<div class="att-box"><span class="att-label">${label}</span>${body}</div>`;
  }

  // Localização e contato não têm arquivo: caíam no ramo de arquivo e saíam
  // "arquivo indisponível", sumindo com o dado. Mesma leitura das bolhas
  // Location.vue / Contact.vue.
  if (attachment.file_type === 'location') {
    const lat = Number(attachment.coordinates_lat);
    const long = Number(attachment.coordinates_long);
    // A coluna tem default 0.0: 0,0 é "sem coordenada", não um ponto no mar.
    const hasCoords =
      Number.isFinite(lat) &&
      Number.isFinite(long) &&
      (lat !== 0 || long !== 0);
    // No WhatsApp o fallback_title costuma ser as próprias coordenadas; sem
    // título, elas viram o título — nunca as duas repetidas.
    const title =
      (attachment.fallback_title || '').trim() ||
      (hasCoords ? `${lat}, ${long}` : '');
    const titleHtml = title
      ? `<div class="att-transcript">${escapeHtml(title)}</div>`
      : '';
    const mapHtml = hasCoords
      ? `<a href="https://maps.google.com/?q=${lat},${long}" target="_blank" rel="noopener noreferrer">Ver no mapa</a>`
      : '<div class="att-missing">Localização sem coordenadas</div>';
    return `<div class="att-box"><span class="att-label">${label}</span>${titleHtml}${mapHtml}</div>`;
  }

  if (attachment.file_type === 'contact') {
    const { firstName, lastName } = attachment.meta || {};
    const contactName = `${firstName || ''} ${lastName || ''}`.trim();
    const phone = (attachment.fallback_title || '').trim();
    const body =
      [contactName, phone]
        .filter(Boolean)
        .map(part => `<div class="att-transcript">${escapeHtml(part)}</div>`)
        .join('') ||
      '<div class="att-missing">Contato sem nome nem telefone</div>';
    return `<div class="att-box"><span class="att-label">${label}</span>${body}</div>`;
  }

  const name = url ? fileNameFromUrl(url) : '';
  const nameHtml = name
    ? `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(name)}</a>`
    : '<span class="att-missing">arquivo indisponível</span>';
  return `<div class="att-box"><span class="att-label">${label}</span> ${nameHtml}</div>`;
};

const buildMessageCell = msg => {
  const text = msg.content
    ? `<div class="msg-text">${escapeHtml(msg.content)}</div>`
    : '';
  const attachments = (msg.attachments || []).map(renderAttachment).join('');
  return text + attachments;
};

const buildMessageRows = messages => {
  if (!messages || messages.length === 0) return '';

  return messages
    .filter(msg => !msg.private)
    .map(msg => {
      const isOutgoing = msg.message_type === MESSAGE_TYPE.OUTGOING;
      const isActivity = msg.message_type === MESSAGE_TYPE.ACTIVITY;

      if (isActivity) {
        return `
        <tr class="activity-row">
          <td colspan="3">
            <div class="activity-pill">${escapeHtml(msg.content)}</div>
          </td>
        </tr>`;
      }

      const senderName = isOutgoing
        ? escapeHtml(msg.sender?.name || 'Agente')
        : escapeHtml(msg.sender?.name || 'Contato');

      const badgeClass = isOutgoing ? 'badge-out' : 'badge-in';
      const badgeLabel = isOutgoing ? 'Saída' : 'Entrada';
      const initials = avatarInitials(senderName);
      const avatarClass = isOutgoing ? 'avatar-out' : 'avatar-in';

      return `
      <tr class="msg-row">
        <td class="td-date">${formatDate(msg.created_at)}</td>
        <td class="td-sender">
          <div class="sender-wrap">
            <div class="avatar ${avatarClass}">${initials}</div>
            <div>
              <div class="sender-name">${senderName}</div>
              <span class="badge ${badgeClass}">${badgeLabel}</span>
            </div>
          </div>
        </td>
        <td class="td-msg">${buildMessageCell(msg)}</td>
      </tr>`;
    })
    .join('');
};

export const buildConversationHtml = (
  conversation,
  messages,
  inboxName = ''
) => {
  const dark = isDarkMode();
  const C = dark ? THEMES.dark : THEMES.light;
  const contact = conversation.meta?.sender?.name || 'Contato';
  const inbox = inboxName;
  const conversationId = conversation.id;
  const createdAt = formatDate(conversation.created_at);
  const exportedAt = new Date().toLocaleString('pt-BR');

  const html = `<!DOCTYPE html>
<html lang="pt-BR"${dark ? ' class="dark"' : ''}>
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Conversa #${conversationId}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap" rel="stylesheet">
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      background: ${C.bg};
      color: ${C.textPrimary};
      font-size: 14px;
      line-height: 1.5;
      min-height: 100vh;
    }

    .page {
      max-width: 860px;
      margin: 0 auto;
      padding: 32px 24px 48px;
    }

    /* ── Header ── */
    .header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      margin-bottom: 24px;
    }
    .header-left { display: flex; flex-direction: column; gap: 4px; }
    .header-eyebrow {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: .06em;
      color: ${C.primary};
    }
    .header-title {
      font-size: 20px;
      font-weight: 600;
      color: ${C.textPrimary};
      line-height: 1.3;
    }
    .header-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-top: 6px;
    }
    .meta-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      background: ${C.surfaceAlt};
      border: 1px solid ${C.border};
      border-radius: 6px;
      padding: 3px 10px;
      font-size: 12px;
      color: ${C.textSecondary};
    }
    .meta-chip b { color: ${C.textPrimary}; font-weight: 500; }

    /* ── Print button ── */
    .print-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: ${C.primary};
      color: #fff;
      border: none;
      border-radius: 8px;
      padding: 8px 16px;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      font-family: inherit;
      white-space: nowrap;
    }
    .print-btn:hover { background: ${C.primaryHover}; }

    /* ── Divider ── */
    .divider {
      height: 1px;
      background: ${C.border};
      margin-bottom: 20px;
    }

    /* ── Table ── */
    .msg-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      background: ${C.surface};
      border: 1px solid ${C.border};
      border-radius: 12px;
      overflow: hidden;
    }

    .msg-table thead tr {
      background: ${C.surfaceAlt};
    }
    .msg-table th {
      padding: 10px 14px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: .05em;
      color: ${C.textMuted};
      text-align: left;
      border-bottom: 1px solid ${C.border};
    }

    .msg-row td { border-bottom: 1px solid ${C.borderSubtle}; vertical-align: top; }
    .msg-row:last-child td { border-bottom: none; }
    .msg-row:hover td { background: ${C.surfaceAlt}; }

    .td-date {
      padding: 12px 14px;
      font-size: 12px;
      color: ${C.textMuted};
      white-space: nowrap;
      width: 148px;
    }
    .td-sender {
      padding: 12px 14px;
      width: 170px;
    }
    .sender-wrap { display: flex; align-items: flex-start; gap: 8px; }
    .avatar {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 11px;
      font-weight: 600;
      flex-shrink: 0;
    }
    .avatar-out { background: ${C.badgeOutBg}; color: ${C.badgeOutText}; }
    .avatar-in  { background: ${C.badgeInBg};  color: ${C.badgeInText}; }
    .sender-name { font-size: 13px; font-weight: 500; color: ${C.textPrimary}; line-height: 1.3; }
    .badge {
      display: inline-block;
      font-size: 10px;
      font-weight: 500;
      padding: 1px 6px;
      border-radius: 99px;
      margin-top: 3px;
    }
    .badge-out { background: ${C.badgeOutBg}; color: ${C.badgeOutText}; }
    .badge-in  { background: ${C.badgeInBg};  color: ${C.badgeInText}; }

    .td-msg {
      padding: 12px 14px;
      font-size: 13px;
      color: ${C.textPrimary};
      word-break: break-word;
    }
    .msg-text { white-space: pre-wrap; }
    .msg-text + .att-box, .msg-text + a, .att-box + .att-box { margin-top: 8px; }
    .att-img {
      display: block;
      max-width: 100%;
      max-height: 320px;
      border-radius: 8px;
      border: 1px solid ${C.border};
      margin-top: 4px;
    }
    .att-box {
      background: ${C.surfaceAlt};
      border: 1px solid ${C.borderSubtle};
      border-radius: 8px;
      padding: 6px 10px;
    }
    .att-label {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: .05em;
      color: ${C.textMuted};
    }
    .att-box a { color: ${C.primary}; }
    .att-transcript { white-space: pre-wrap; margin-top: 2px; }
    .att-missing { font-style: italic; color: ${C.textMuted}; margin-top: 2px; }

    /* ── Activity row ── */
    .activity-row td { padding: 6px 14px; border-bottom: 1px solid ${C.borderSubtle}; }
    .activity-row:last-child td { border-bottom: none; }
    .activity-pill {
      text-align: center;
      font-size: 11px;
      color: ${C.activityText};
      background: ${C.activityBg};
      border-radius: 99px;
      padding: 3px 12px;
      display: inline-block;
      width: 100%;
    }

    /* ── Empty state ── */
    .empty {
      text-align: center;
      padding: 40px;
      color: ${C.textMuted};
      font-size: 13px;
    }

    /* ── Footer ── */
    .footer {
      margin-top: 20px;
      font-size: 11px;
      color: ${C.textMuted};
      text-align: right;
    }

    /* ── Print ── */
    @media print {
      .no-print { display: none !important; }
      body { background: ${dark ? C.bg : '#fff'}; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page { padding: 16px; }
      .msg-table { border: 1px solid ${C.border}; }
      .msg-row:hover td { background: none; }
    }
  </style>
</head>
<body>
<div class="page">

  <div class="header">
    <div class="header-left">
      <span class="header-eyebrow">Conversa exportada</span>
      <h1 class="header-title">#${conversationId} &mdash; ${escapeHtml(contact)}</h1>
      <div class="header-meta">
        <span class="meta-chip">ID <b>#${conversationId}</b></span>
        ${inbox ? `<span class="meta-chip">Canal <b>${escapeHtml(inbox)}</b></span>` : ''}
        ${createdAt ? `<span class="meta-chip">Iniciada em <b>${createdAt}</b></span>` : ''}
        <span class="meta-chip">${messages.filter(m => !m.private && m.message_type !== MESSAGE_TYPE.ACTIVITY).length} mensagens</span>
      </div>
    </div>
    <button class="print-btn no-print" onclick="printWhenReady()">
      &#8595; Salvar como PDF
    </button>
  </div>

  <div class="divider"></div>

  <table class="msg-table">
    <thead>
      <tr>
        <th>Data / Hora</th>
        <th>Remetente</th>
        <th>Mensagem</th>
      </tr>
    </thead>
    <tbody>
      ${buildMessageRows(messages) || `<tr><td colspan="3"><div class="empty">Nenhuma mensagem encontrada.</div></td></tr>`}
    </tbody>
  </table>

  <div class="footer">
    Exportado em ${exportedAt} &bull; Cortex
  </div>

</div>
<script>
  // Imagem que ainda não carregou sai em branco no PDF: espera todas antes
  // de abrir o diálogo de impressão.
  function printWhenReady() {
    var pending = Array.prototype.filter
      .call(document.images, function (img) { return !img.complete; })
      .map(function (img) {
        return new Promise(function (resolve) { img.onload = img.onerror = resolve; });
      });
    Promise.all(pending).then(function () { window.print(); });
  }
</script>
</body>
</html>`;

  return html;
};

// Abre a janela no clique (síncrono): depois do await da busca de mensagens o
// gesto do usuário já expirou e o bloqueador de pop-up barraria a abertura.
// Devolve null se o navegador bloqueou — quem chama avisa o atendente.
export const openPdfWindow = () => {
  const win = window.open(
    '',
    '_blank',
    'width=960,height=720,left=100,top=80,resizable=yes,scrollbars=yes'
  );
  if (win) {
    win.document.title = 'Exportando conversa…';
    win.document.body.style.fontFamily = 'sans-serif';
    win.document.body.textContent = 'Carregando a conversa…';
  }
  return win;
};

export const renderConversationPdf = (
  win,
  conversation,
  messages,
  inboxName = ''
) => {
  const html = buildConversationHtml(conversation, messages, inboxName);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  win.location.replace(url);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};
